/**
 * DogStatusStrip (#546 → Ted #28 Phase 2, #1050) — the small strip under a
 * dog's name.
 *
 * It used to draw four private badges (the vax syringe, the sex/altered
 * glyph, the balance `$`, the store-credit amount). Phase 2 folds every
 * status into the ONE flag registry (`dog-flags.ts`) and this strip draws
 * exactly two things (plus, Sawyer #1295, the "Ate none" badge first when
 * the row carries one):
 *   (1) the dog's and the stay's flags through `DogFlagChips` — the vaccine
 *       syringe (ORANGE expiring / RED expired / outlined "no records"),
 *       INTACT, meds, transport — severity-first so the most urgent pill
 *       reads first at 375px;
 *   (2) the sex glyph ♂/♀ with the altered abbreviation (S/N/A) — derived
 *       server-side, NO glyph when either is unknown (never guessed). There
 *       is deliberately no `I`: intact is the registry's loud INTACT pill,
 *       not a one-letter hint (Ted: "very obvious").
 *
 * The owner's flags (balance / credit / agreement) are NOT drawn here: Ted
 * puts money "underneath the client's name", so the caller renders
 * `<DogFlagChips scope="account">` at the owner position (DogFirstCell,
 * FrontDesk). `flag-surface-coverage.test.ts` enforces both halves.
 *
 * Deploy window: `resolveDogFlags` fills what a Phase 1 server only says the
 * legacy way (`vax.level`, `unpaid`, `walletBalanceCents`, `hasMedications`)
 * so the desk never loses a badge between the frontend deploy and the
 * server deploy. Delete with `flag-skew.ts` once the server is live.
 *
 * Accessibility (DESIGN-PHILOSOPHY): every pill carries its label as text
 * and its detail as title + aria-label; colour is never the only signal.
 */
import type { DogCard } from '../../lib/api.js';
import { DogFlagChips } from './DogFlagChips.js';
import { resolveDogFlags, type LegacyRowFacts } from './flag-skew.js';
import { AteNoneBadge, parseAteNone } from '../care-log/AteNoneBadge.js';

interface DogStatusStripProps {
  dog: DogCard;
  /**
   * The row's LEGACY facts (deploy-window shim only). Passed by the lead
   * dog's caller; the merged list is what `DogFlagChips` draws. Harmless on
   * a Phase 2 server: the registry already carries every key.
   */
  legacy?: LegacyRowFacts;
  /** Test-id prefix so a surface's pills can be found in its own tests. */
  testIdPrefix?: string;
}

const SEX_GLYPH: Record<NonNullable<DogCard['sex']>, string> = {
  male: '♂',
  female: '♀',
};

/**
 * Short altered abbreviation beside the sex glyph. `intact` is deliberately
 * absent — the registry's INTACT pill carries it (Ted #28 Phase 2).
 */
const ALTERED_ABBR: Partial<Record<NonNullable<DogCard['altered']>, string>> = {
  spayed: 'S',
  neutered: 'N',
  altered: 'A',
};

const ALTERED_WORD: Record<NonNullable<DogCard['altered']>, string> = {
  intact: 'intact',
  spayed: 'spayed',
  neutered: 'neutered',
  altered: 'altered',
};

function SexAltered({ dog }: { dog: DogCard }): JSX.Element | null {
  // Honesty (#546): no glyph at all when sex is unknown — never guess.
  if (dog.sex === null) return null;
  const sexWord = dog.sex === 'male' ? 'Male' : 'Female';
  const alteredWord = dog.altered ? `, ${ALTERED_WORD[dog.altered]}` : '';
  const label = `${sexWord}${alteredWord}`;
  const abbr = dog.altered ? ALTERED_ABBR[dog.altered] : undefined;
  return (
    <span
      className={`ferro-dog-strip__sex ferro-dog-strip__sex--${dog.sex}`}
      role="img"
      aria-label={label}
      title={label}
      data-testid="sex-altered-glyph"
    >
      <span aria-hidden="true" className="ferro-dog-strip__sex-symbol">
        {SEX_GLYPH[dog.sex]}
      </span>
      {abbr ? (
        <span aria-hidden="true" className="ferro-dog-strip__sex-abbr">
          {abbr}
        </span>
      ) : null}
    </span>
  );
}

export function DogStatusStrip({ dog, legacy, testIdPrefix }: DogStatusStripProps): JSX.Element | null {
  const flags = resolveDogFlags(dog, legacy);
  const hasDogFlags = flags.some((f) => f.scope === 'dog' || f.scope === 'stay');
  // Sawyer #1295 — `/today` rows carry the dog's "Ate none" meals; every
  // other surface (and an older server) sends none and nothing renders.
  const ateNone = parseAteNone(dog.ateNone);
  // Nothing to show → render nothing (no empty strip taking vertical space).
  if (!hasDogFlags && dog.sex === null && ateNone === null) return null;
  return (
    <span className="ferro-dog-strip" data-testid="dog-status-strip">
      {/* Sawyer #1295 — first: the most urgent thing about this dog today. */}
      <AteNoneBadge ateNone={ateNone} dogName={dog.name} testId={`${testIdPrefix ?? ''}ate-none-${dog.dogId}`} />
      <DogFlagChips flags={flags} scope="dog" testIdPrefix={testIdPrefix} />
      <SexAltered dog={dog} />
    </span>
  );
}
